import React, { useState, useRef, useEffect } from 'react';
import { createPortal } from 'react-dom';

/**
 * Lightweight hover tooltip. Wrap a trigger element; `text` appears in a
 * dark bubble ~150 ms after hover. Rendered via a portal to document.body so
 * it escapes `overflow: hidden` and `transform`ed ancestors (which would
 * otherwise re-anchor `position: fixed` to themselves).
 */
export default function Tooltip({ text, children, placement = 'top' }) {
  const [open, setOpen] = useState(false);
  const [coords, setCoords] = useState({ top: 0, left: 0 });
  const timerRef = useRef(null);
  const triggerRef = useRef(null);

  useEffect(() => () => clearTimeout(timerRef.current), []);

  // Dismiss on outside tap while open (touch devices)
  useEffect(() => {
    if (!open) return;
    const onDocPointer = (e) => {
      if (triggerRef.current && !triggerRef.current.contains(e.target)) {
        clearTimeout(timerRef.current);
        setOpen(false);
      }
    };
    document.addEventListener('pointerdown', onDocPointer);
    return () => document.removeEventListener('pointerdown', onDocPointer);
  }, [open]);

  const show = () => {
    clearTimeout(timerRef.current);
    timerRef.current = setTimeout(() => {
      if (triggerRef.current) {
        const rect = triggerRef.current.getBoundingClientRect();
        const top = placement === 'top' ? rect.top - 8 : rect.bottom + 8;
        const left = rect.left + rect.width / 2;
        setCoords({ top, left });
        setOpen(true);
      }
    }, 150);
  };

  const hide = () => {
    clearTimeout(timerRef.current);
    setOpen(false);
  };

  // Toggle on tap, but DO NOT stop propagation — Tooltip is often nested
  // inside clickable elements (activity chips) whose click must still fire.
  const toggle = () => {
    if (open) hide();
    else show();
  };

  if (!text) return children;

  return (
    <>
      <span
        ref={triggerRef}
        onMouseEnter={show}
        onMouseLeave={hide}
        onFocus={show}
        onBlur={hide}
        onClick={toggle}
        style={{ display: 'inline-flex' }}
      >
        {children}
      </span>
      {open && createPortal(
        <div
          role="tooltip"
          style={{
            position: 'fixed',
            top: coords.top,
            left: coords.left,
            transform: placement === 'top' ? 'translate(-50%, -100%)' : 'translate(-50%, 0)',
            backgroundColor: '#111827',
            color: 'white',
            padding: '6px 10px',
            borderRadius: '6px',
            fontSize: '12px',
            lineHeight: 1.4,
            width: 'max-content',
            maxWidth: '260px',
            textAlign: 'center',
            whiteSpace: 'normal',
            pointerEvents: 'none',
            zIndex: 9999,
            boxShadow: '0 4px 12px rgba(0,0,0,0.25)',
          }}
        >
          {text}
        </div>,
        document.body
      )}
    </>
  );
}
